export const WELCOME_DIALOG_SESSION_KEY = 'welcome-dialog-dismissed';

/**
 * Espelho do "não mostrar novamente" nesta aba. A coluna em `platform_user` é a preferência de verdade, mas a
 * sessão do NextAuth é cacheada no cliente e a 4.19.2 não expõe `update()`: sem o espelho, o diálogo voltaria
 * assim que o `Layout` que o monta desmontasse — o próprio "Começar" leva a uma página de outro layout.
 *
 * Duas camadas porque `sessionStorage` pode lançar (storage bloqueado, cota cheia): a memória cobre a
 * remontagem, o storage cobre o refresh. Nenhuma sobrevive à aba de propósito — se o PATCH falhou, a próxima
 * sessão mostra o diálogo outra vez. As duas guardam o id do dono porque logout não limpa nem uma nem outra.
 */
let dismissedOwner: number | null = null;

export const getVisitWelcomeDialogDismissal = (
  userId: number | undefined,
): boolean => {
  if (!userId) return false;
  if (dismissedOwner === userId) return true;

  try {
    const stored = JSON.parse(
      sessionStorage.getItem(WELCOME_DIALOG_SESSION_KEY) ?? 'null',
    );

    return stored?.userId === userId;
  } catch {
    return false;
  }
};

export const setVisitWelcomeDialogDismissal = (userId: number | undefined) => {
  if (!userId) return;

  dismissedOwner = userId;

  try {
    sessionStorage.setItem(
      WELCOME_DIALOG_SESSION_KEY,
      JSON.stringify({ userId }),
    );
  } catch {
    // Só o refresh fica descoberto; a remontagem continua atendida pela memória.
  }
};

export const WELCOME_DIALOG_POSTPONED_KEY = 'welcome-dialog-postponed';

/**
 * "Criar comunidade" adia o convite até o próximo login. Fica no `localStorage` para valer em todas as abas
 * e é apagado pelo login; guarda o id do dono porque logout não limpa nada.
 */
let postponedOwner: number | null = null;

export const getWelcomeDialogPostponement = (
  userId: number | undefined,
): boolean => {
  if (!userId) return false;
  if (postponedOwner === userId) return true;

  try {
    const stored = JSON.parse(
      localStorage.getItem(WELCOME_DIALOG_POSTPONED_KEY) ?? 'null',
    );

    return stored?.userId === userId;
  } catch {
    return false;
  }
};

export const setWelcomeDialogPostponement = (userId: number | undefined) => {
  if (!userId) return;

  postponedOwner = userId;

  try {
    localStorage.setItem(
      WELCOME_DIALOG_POSTPONED_KEY,
      JSON.stringify({ userId }),
    );
  } catch {
    // A memória ainda cobre a navegação até o próximo refresh.
  }
};

export const clearWelcomeDialogPostponement = () => {
  postponedOwner = null;

  try {
    localStorage.removeItem(WELCOME_DIALOG_POSTPONED_KEY);
  } catch {
    // Sem storage, não há marca gravada para apagar.
  }
};
